<script lang="ts" setup>
import type { Constant } from '@sancare/typescript-commons/src/store/modules/remote-lists/types'
import type { FilterEvent } from '@sancare/typescript-commons/src/types/filters'
import { computed, type ComputedRef, onMounted, type Ref, ref, watch } from 'vue'
import VueSlider from 'vue-slider-component'
import { useStore } from 'vuex'

const OFFSET_CONSTANT_VALUE_RANGE = 10

interface Range {
  min: string | number
  max: string | number
  list: (string | number)[]
}

const store = useStore()

const model = defineModel<string | null>({ default: null })
const props = withDefaults(defineProps<{
  enableOkBtn?: boolean
  enableRangeCondition?: boolean
}>(), {
  enableOkBtn: true,
  enableRangeCondition: true,
})

const emit = defineEmits<{
  (e: 'add-filter', filter: FilterEvent): void
}>()


const constantDescription = ref<string | null>(null)
const inputMode = ref<'slider' | 'manual'>('slider')
const values: Ref<(number | string)[]> = ref([0, 100])
const range: Ref<Range> = ref({ min: 0, max: 100, list: [0, 100] })

const constants: ComputedRef<Constant[]> = computed(() => store.state.remoteLists.constants)

function initializeFromInitialValue() {
  if (!model.value) return

  const parts = model.value.split('\x1F')
  if (parts.length < 3) return

  const [description, min, max] = parts
  constantDescription.value = description

  const newMin = min.includes('<') || min.includes('>') ? min : Number(min)
  const newMax = max.includes('>') || max.includes('<') ? max : Number(max)

  updateRange(newMin, newMax)

  values.value = [newMin, newMax]
}

function updateRange(initialMin?: number | string, initialMax?: number | string) {
  if (!constantDescription.value) return

  const constant = constants.value.find((constant) => constant.description === constantDescription.value)
  if (!constant) return

  const rawMin = parseFloat(constant.min)
  const rawMax = parseFloat(constant.max)

  let min = (rawMin - OFFSET_CONSTANT_VALUE_RANGE * rawMin / 100) + 0.5
  let max = (rawMax + OFFSET_CONSTANT_VALUE_RANGE * rawMax / 100) + 0.5

  min -= min % 10
  max += 10 - (max % 10)

  if (min < 0 && rawMin >= 0) {
    min = 0
  }

  const diff = max - min
  let step = 1

  if (diff > 1000) step = 50
  else if (diff > 100) step = 5
  else if (diff < 1 && diff > 0.1) step = 0.05
  else if (diff < 1 && diff > 0.01) step = 0.005

  const list: (number | string)[] = []
  let i = min

  if (min !== 0 && rawMin >= 0) {
    list.push(`<${min}`)
  }

  while (i < max) {
    list.push(i)
    i += step
  }

  if (typeof initialMin === 'number' && initialMin >= min) list.push(initialMin)
  if (typeof initialMax === 'number' && initialMax <= max) list.push(initialMax)

  list.sort((a, b) => {
    if (typeof a === 'string' && a.includes('<')) return -1
    if (typeof b === 'string' && b.includes('>')) return 1
    return Number(a) - Number(b)
  })

  list.push(max)
  list.push(`>${max}`)
  if (typeof initialMax === 'number' && initialMax > max) list.push(initialMax)

  if (min !== 0 && rawMin >= 0) {
    values.value = [`<${min}`, `>${max}`]
    range.value = { min: `<${min}`, max: `>${max}`, list }
  } else {
    values.value = [min, `>${max}`]
    range.value = { min, max: `>${max}`, list }
  }
}

watch(constantDescription, () => {
  if (!constantDescription.value) return

  if (!props.enableRangeCondition) {
    addFilter()
    return
  }

  if (!model.value) {
    updateRange()
  }
})

watch(values, () => {
  if (!props.enableOkBtn) {
    addFilter()
  }
}, { deep: true })

function addFilter() {
  if (!constantDescription.value) return

  let value = constantDescription.value

  if (props.enableRangeCondition) {
    value += `\x1F${values.value[0]}\x1F${values.value[1]}`
  }

  emit('add-filter', {
    name: 'healthConstant',
    type: 'healthConstant',
    value
  })
}

function toggleInputMode() {
  inputMode.value = inputMode.value === 'slider' ? 'manual' : 'slider'
}

onMounted(() => {
  initializeFromInitialValue()
})
</script>

<template>
  <div>
    <div class="row align-items-center">
      <div class="col">
        <select
          id="constant-filter-select"
          v-model="constantDescription"
          class="form-select"
        >
          <option
            value=""
            disabled
            selected
          >
            ---
          </option>
          <option
            v-for="constant in constants"
            :key="'constant_' + constant.description"
            :value="constant.description"
          >
            {{ constant.description }}
          </option>
        </select>
      </div>
    </div>
    <div
      v-if="constantDescription && enableRangeCondition"
      class="row align-items-center"
      :class="inputMode === 'slider' ? 'mt-5' : 'mt-3'"
    >
      <div class="col">
        <vue-slider
          v-if="inputMode === 'slider'"
          v-model="values"
          :v-data="range.list"
          :tooltip="'always'"
          range
          class="mx-3"
        />
        <div
          v-else
          class="row align-items-center"
        >
          <div class="col-auto text-center">
            de
          </div>
          <div class="col">
            <input
              id="constant-filter-range-min"
              v-model="values[0]"
              type="text"
              class="form-control"
            >
          </div>
          <div class="col-auto text-center">
            à
          </div>
          <div class="col">
            <input
              id="constant-filter-range-max"
              v-model="values[1]"
              type="text"
              class="col form-control"
            >
          </div>
        </div>
      </div>
      <div
        v-if="enableOkBtn"
        class="col-auto"
      >
        <button
          id="constant-filter-add"
          class="btn btn-primary"
          type="button"
          @click="addFilter"
        >
          Ok
        </button>
      </div>
      <div class="row col-12 ms-2">
        <span
          id="constant-filter-toggle-mode"
          class="btn btn-link"
          @click="toggleInputMode"
        >
          {{ inputMode === 'slider' ? 'Mode manuel' : 'Mode normal' }}
        </span>
      </div>
    </div>
  </div>
</template>
